import { useState } from "react";
import * as Icons from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { STATUS_META, SYNC_META, toDisplay, parseFr } from "@/lib/helpers";

export function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.brouillon;
  return (
    <Badge variant="outline" data-testid={`status-badge-${status}`}
      className={`gap-1.5 font-medium ${m.badge}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </Badge>
  );
}

export function SyncBadge({ state }) {
  const m = SYNC_META[state] || SYNC_META.local;
  const Ico = state === "synced" ? Icons.CloudCheck : state === "error" ? Icons.CloudOff
    : state === "conflict" ? Icons.GitMerge : state === "offline" ? Icons.CloudOff
    : state === "syncing" ? Icons.RefreshCw : Icons.Save;
  return (
    <span data-testid="sync-indicator"
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${m.cls}`}>
      <Ico className={`h-3.5 w-3.5 ${state === "syncing" ? "animate-spin" : ""}`} />
      {m.label}
    </span>
  );
}

export function PrestIcon({ name, className }) {
  const Ico = Icons[name] || Icons.Square;
  return <Ico className={className} />;
}

// Champ numérique acceptant la virgule décimale française, refuse le négatif.
export function NumberField({ label, value, unit, integer, onChange, testId, placeholder, disabled }) {
  const [display, setDisplay] = useState(toDisplay(value));
  const handle = (e) => {
    let raw = e.target.value.replace(/[^0-9.,]/g, "");
    setDisplay(raw);
    onChange(parseFr(raw));
  };
  return (
    <div className="space-y-1.5">
      {label && <Label className="text-xs text-slate-600">{label}{unit ? ` (${unit})` : ""}</Label>}
      <div className="relative">
        <Input inputMode="decimal" value={display} onChange={handle} data-testid={testId} disabled={disabled}
          placeholder={placeholder || (integer ? "0" : "0,00")}
          className="font-mono-data" />
        {unit && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{unit}</span>}
      </div>
    </div>
  );
}

export function TriState({ value, onChange, testId }) {
  const opts = [{ v: "oui", l: "Oui" }, { v: "non", l: "Non" }, { v: "a_confirmer", l: "À confirmer" }];
  return (
    <div className="flex gap-2" data-testid={testId}>
      {opts.map((o) => (
        <button key={o.v} type="button" onClick={() => onChange(o.v)}
          data-testid={`${testId}-${o.v}`}
          className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
            value === o.v ? "border-emerald-600 bg-emerald-50 text-emerald-700"
              : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

export function BoolSwitch({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
      <Label className="text-sm text-slate-700">{label}</Label>
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

export function EmptyState({ icon = "Inbox", title, children, testId }) {
  const Ico = Icons[icon] || Icons.Inbox;
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-14 text-center" data-testid={testId}>
      <Ico className="mb-3 h-10 w-10 text-slate-300" />
      <p className="font-display text-lg font-semibold text-slate-700">{title}</p>
      {children && <div className="mt-1 max-w-sm text-sm text-slate-500">{children}</div>}
    </div>
  );
}
